import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { logger } from '@/utils/logger';

import type { ControlState, VideoState } from '../types/videoStream';

import { useMqttVideoSubscription } from './useMqttVideoSubscription';
import { useVideoStreamApi } from './useVideoStreamApi';

interface UseVideoStreamParams {
    cageId: number;
    bioscopeId?: number;
}

interface UseVideoStreamReturn {
    videoState: VideoState;
    controlState: ControlState;
    startStream: () => Promise<void>;
    stopStream: () => Promise<void>;
}

const ERROR_CHECK_TIMEOUT_MS = 70000;
const BUTTON_COOLDOWN_MS = 5000;

export function useVideoStream(params: UseVideoStreamParams): UseVideoStreamReturn {
    const { bioscopeId } = params;

    // DEBUG: Track renders
    const hookRenderCountRef = useRef(0);
    hookRenderCountRef.current += 1;
    logger.dev('useVideoStream', 'Render #', hookRenderCountRef.current, { bioscopeId });

    // Video stream state
    const [videoState, setVideoState] = useState<VideoState>({
        status: 'disabled',
    });

    // Control state
    const [controlState, setControlState] = useState<ControlState>({
        isOnline: undefined,
        isButtonDisabled: true,
        isOutOfQuota: false,
        isLoading: false,
    });

    // API hooks with stable references
    const { api } = useVideoStreamApi({ bioscopeId });

    // DEBUG: Track when api reference changes
    const prevApiRef = useRef(api);
    if (prevApiRef.current !== api) {
        logger.dev('useVideoStream', 'API reference changed!');
        prevApiRef.current = api;
    }

    // Refs for timeouts
    const errorCheckTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const buttonCooldownTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Ref for accessing current video state in timeouts
    const videoStateRef = useRef(videoState);
    videoStateRef.current = videoState;

    // Flag to ignore MQTT livestream status changes after user requests stop
    // This prevents MQTT messages (disconnected, idle) from overwriting the 'disabled' state
    const isStoppingRef = useRef(false);

    // Cleanup timeouts on unmount
    useEffect(() => {
        return () => {
            if (errorCheckTimeoutRef.current) {
                clearTimeout(errorCheckTimeoutRef.current);
            }
            if (buttonCooldownTimeoutRef.current) {
                clearTimeout(buttonCooldownTimeoutRef.current);
            }
        };
    }, []);

    // Cleanup: stop stream on unmount or bioscopeId change
    useEffect(() => {
        return () => {
            if (bioscopeId && videoStateRef.current.status === 'active') {
                // Guard the result: a cleanup callback must never throw. If stopStream
                // returns void (e.g. a mocked client), there is no promise to catch.
                api.stopStream()?.catch(console.error);
            }
        };
    }, [bioscopeId, api]);

    // MQTT callbacks - these will be stored in a ref inside useMqttVideoSubscription
    const mqttCallbacks = useMemo(
        () => ({
            onBioscopeStatusChange: async (isOnline: boolean) => {
                setControlState((prev) => ({ ...prev, isOnline }));

                if (!isOnline) {
                    setControlState((prev) => ({ ...prev, isButtonDisabled: true }));
                    return;
                }

                // Check quota when bioscope comes online
                try {
                    const quotaResult = await api.checkQuota();
                    const quotaAvailable = quotaResult?.status === 'available';
                    setControlState((prev) => ({
                        ...prev,
                        isOutOfQuota: !quotaAvailable,
                        isButtonDisabled: !quotaAvailable,
                    }));
                } catch (error) {
                    console.error('Error checking quota:', error);
                    setControlState((prev) => ({ ...prev, isButtonDisabled: true }));
                }
            },

            onLivestreamStatusChange: (data: {
                streamId?: number;
                status: VideoState['status'];
                playbackId?: string;
                userId?: number;
            }) => {
                logger.dev('useVideoStream', 'MQTT livestream status change:', data);

                // Ignore MQTT status updates after user requested stop
                // This prevents 'disconnected' and 'idle' from overwriting 'disabled'
                if (isStoppingRef.current) {
                    logger.dev('useVideoStream', 'Ignoring MQTT status - user requested stop');
                    return;
                }

                setVideoState((prev) => ({
                    ...prev,
                    streamId: data.streamId,
                    status: data.status,
                    playbackId: data.playbackId,
                    userId: data.userId,
                }));
            },

            onBioscopeDataChange: (bioscopeData: VideoState['bioscopeData']) => {
                logger.dev('useVideoStream', 'MQTT bioscope data change:', bioscopeData);
                setVideoState((prev) => ({ ...prev, bioscopeData }));
            },
        }),
        [api]
    );

    // Setup MQTT subscriptions
    useMqttVideoSubscription({
        bioscopeId,
        callbacks: mqttCallbacks,
    });

    // Error check: disable stream if not active after timeout
    const scheduleErrorCheck = useCallback(() => {
        if (errorCheckTimeoutRef.current) {
            clearTimeout(errorCheckTimeoutRef.current);
        }

        errorCheckTimeoutRef.current = setTimeout(() => {
            if (videoStateRef.current.status !== 'active') {
                api.disableStream().catch(console.error);
                setVideoState((prev) => ({ ...prev, status: 'idle' }));
            }
        }, ERROR_CHECK_TIMEOUT_MS);
    }, [api]);

    const clearErrorCheck = useCallback(() => {
        if (errorCheckTimeoutRef.current) {
            clearTimeout(errorCheckTimeoutRef.current);
            errorCheckTimeoutRef.current = null;
        }
    }, []);

    // Temporarily disable button after action
    const tempDisableButton = useCallback(() => {
        setControlState((prev) => ({ ...prev, isButtonDisabled: true }));

        buttonCooldownTimeoutRef.current = setTimeout(() => {
            setControlState((prev) => {
                if (prev.isOnline && !prev.isOutOfQuota) {
                    return { ...prev, isButtonDisabled: false };
                }
                return prev;
            });
        }, BUTTON_COOLDOWN_MS);
    }, []);

    // Start stream handler
    const startStream = useCallback(async () => {
        if (!bioscopeId || controlState.isButtonDisabled || controlState.isLoading) {
            return;
        }

        // Reset the stopping flag so MQTT status changes are processed again
        isStoppingRef.current = false;
        logger.dev('useVideoStream', 'isStoppingRef reset to false (starting stream)');

        setControlState((prev) => ({
            ...prev,
            isLoading: true,
            isButtonDisabled: true,
        }));
        setVideoState((prev) => ({ ...prev, status: 'idle' }));

        try {
            await api.startStream();
            scheduleErrorCheck();
            tempDisableButton();
        } catch (error) {
            console.error('Error starting stream:', error);
            setVideoState((prev) => ({ ...prev, status: 'error' }));
            setControlState((prev) => ({
                ...prev,
                isLoading: false,
                isButtonDisabled: false,
            }));
        }
    }, [
        bioscopeId,
        controlState.isButtonDisabled,
        controlState.isLoading,
        api,
        scheduleErrorCheck,
        tempDisableButton,
    ]);

    // Stop stream handler
    const stopStream = useCallback(async () => {
        logger.dev('useVideoStream', 'stopStream called', { bioscopeId });
        if (!bioscopeId) {
            logger.dev('useVideoStream', 'stopStream aborted - no bioscopeId');
            return;
        }

        // Set flag to ignore incoming MQTT status changes
        isStoppingRef.current = true;
        logger.dev('useVideoStream', 'isStoppingRef set to true');

        try {
            logger.dev('useVideoStream', 'Calling api.stopStream()');
            await api.stopStream();
            clearErrorCheck();
            logger.dev('useVideoStream', 'Setting videoState to disabled');
            setVideoState((prev) => ({ ...prev, status: 'disabled', playbackId: undefined }));
            // Reset control state to allow restarting the stream
            setControlState((prev) => ({
                ...prev,
                isLoading: false,
                isButtonDisabled: !(prev.isOnline && !prev.isOutOfQuota),
            }));
        } catch (error) {
            logger.dev('useVideoStream', 'Error stopping stream:', error);
            // Reset the flag on error so MQTT can work again
            isStoppingRef.current = false;
            // Also reset control state on error to allow retry
            setControlState((prev) => ({
                ...prev,
                isLoading: false,
                isButtonDisabled: !(prev.isOnline && !prev.isOutOfQuota),
            }));
        }
    }, [bioscopeId, api, clearErrorCheck]);

    // DEBUG: Track when stopStream reference changes
    const prevStopStreamRef = useRef(stopStream);
    if (prevStopStreamRef.current !== stopStream) {
        logger.dev('useVideoStream', 'stopStream reference changed!');
        prevStopStreamRef.current = stopStream;
    }

    return {
        videoState,
        controlState,
        startStream,
        stopStream,
    };
}
